import { configureStore } from '@reduxjs/toolkit/react'

import { IApi as IAltApi } from '@/spi/api'
import { AdminState } from '@/store/admin'
import { AlertState } from '@/store/alert'
import { AssistantState } from '@/store/assistant'
import { authMiddleware } from '@/store/auth/middleware'
import { AuthState } from '@/store/auth/slice'
import { BeneficiaryState } from '@/store/beneficiary'
import { beneficiaryListFiltersMiddleware } from '@/store/beneficiary-list-filters/middleware'
import { BeneficiaryListState } from '@/store/beneficiary-list-filters/slice'
import { CaseWorkerListState } from '@/store/case-workers/slice'
import { ChatState } from '@/store/chat'
import { dashboardTasksMiddleware } from '@/store/dashboard-tasks/middleware'
import { DashboardTasksState } from '@/store/dashboard-tasks/slice'
import { dashboardWidgetsMiddleware } from '@/store/dashboard-widgets/middleware'
import { DashboardWidgetsState } from '@/store/dashboard-widgets/slice'
import { DemoState } from '@/store/demo'
import { DocumentState } from '@/store/document'
import { effectsMiddleware } from '@/store/middlewares/effects'
import { socketIOMiddleware } from '@/store/middlewares/socket'
import { staticSocketIOMiddleware } from '@/store/middlewares/static-socket'
import { trackingMiddleware } from '@/store/middlewares/tracking.ts'
import { ISocket } from '@/store/middlewares/types'
import { NotificationState } from '@/store/notifications/notification'
import { reportTemplatesMiddleware } from '@/store/report-templates/middleware'
import { ReportTemplatesState } from '@/store/report-templates/slice'
import { rootReducer } from '@/store/root'
import { SignUpState } from '@/store/signup.ts'
import { IAccessTokenManager, IApi } from '@/store/spi/api'
import { ISudoApi } from '@/store/spi/sudo'
import { ITracker } from '@/store/spi/tracker.ts'
import { structuredDataStateMiddleware } from '@/store/structured-data-state/middleware'
import { StructuredDataStateState } from '@/store/structured-data-state/slice'
import { SudoState } from '@/store/sudo'
import { TaskState } from '@/store/task'
import { UserState } from '@/store/user'

import { ThunkExtra } from './actions'
import { BeneficiaryInsightsState } from './beneficiary-insights/slice'
import { caseWorkerPreferencesMiddleware } from './case-worker-preferences/middleware'
import { State } from './case-worker-preferences/types'

export const createStore = ({
  accessTokenManager,
  api,
  altApi,
  initialState,
  socket,
  staticSocket,
  sudoApi,
  tracker,
}: {
  accessTokenManager: IAccessTokenManager
  api: IApi
  altApi: IAltApi
  initialState?: RootState
  socket: ISocket
  staticSocket: ISocket
  sudoApi: ISudoApi
  tracker: ITracker
}) => {
  const extraArgument: ThunkExtra = {
    accessTokenManager,
    api,
    altApi,
    socket,
    sudoApi,
  }

  return configureStore({
    preloadedState: initialState,
    reducer: rootReducer,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        thunk: {
          extraArgument,
        },
      })
        .prepend(
          authMiddleware,
          beneficiaryListFiltersMiddleware,
          dashboardTasksMiddleware,
          dashboardWidgetsMiddleware,
          reportTemplatesMiddleware,
          structuredDataStateMiddleware,
          caseWorkerPreferencesMiddleware(extraArgument), // NOTE: prepend doesn't set/attach the extraArgument to the middleware
        )
        .concat([
          socketIOMiddleware(socket),
          staticSocketIOMiddleware(staticSocket),
          effectsMiddleware(),
          trackingMiddleware(tracker),
        ]),
  })
}

// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = {
  admin: AdminState
  alert: AlertState
  assistant: AssistantState
  auth: AuthState
  beneficiary: BeneficiaryState
  beneficiaryList: BeneficiaryListState
  beneficiaryInsights: BeneficiaryInsightsState
  caseWorkerList: CaseWorkerListState
  caseWorkerPreferences: State
  chat: ChatState
  dashboardTasks: DashboardTasksState
  dashboardWidgets: DashboardWidgetsState
  demo: DemoState
  document: DocumentState
  notification: NotificationState
  reportTemplates: ReportTemplatesState
  signUp: SignUpState
  structuredDataState: StructuredDataStateState
  sudo: SudoState
  task: TaskState
  user: UserState
}
